import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Download } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import PeriodRangePicker, { presetRange } from '../../components/ui/PeriodRangePicker';
import { getExpenseReport, getExpenseReportFilters, exportExpenseReport } from '../../services/expenseReportService';
import { getLocations } from '../../services/locationService';
import { getExpenseCategories } from './reportSections';
import buildSections from './reportSections';

const FilterBar = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  padding: 14px 16px;
  margin: 12px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-start;
`;

const Selects = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  > div { width: 180px; }
`;

const ExportRow = styled.div`
  margin-left: auto;
  display: flex;
  gap: 6px;
`;

const ExportBtn = styled(Button)`
  padding: 7px 12px;
  font-size: 12px;
  border-width: 1.5px;
`;

const KpiRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
`;

const Kpi = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  padding: 10px 16px;

  > div:first-child { font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; }
  > div:last-child { font-size: 18px; font-weight: 800; color: ${({ theme }) => theme.colors.text.primary}; }
`;

const SectionCard = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  padding: 16px 18px;
  margin-bottom: 14px;
  overflow-x: auto;
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    text-align: left;
    padding: 6px 10px;
    background: #F9FAFB;
    color: #6B7280;
    font-size: 11px;
    white-space: nowrap;
  }
  td {
    padding: 6px 10px;
    border-bottom: 1px solid #F0F2F5;
    color: #374151;
    white-space: nowrap;
  }
`;

const Footnote = styled.div`
  font-size: 11px;
  color: #9CA3AF;
  margin: 4px 0;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #9CA3AF;
  font-size: 13px;
`;

const SLUGS = ['department', 'category', 'employee', 'approval-process', 'behavior', 'vendor', 'comparison'];
const DIMENSIONS = ['total', 'category', 'department', 'location', 'employee'];
const URL_FIELDS = ['tab', 'preset', 'from', 'to', 'compareFrom', 'compareTo', 'locationId', 'departmentId', 'employeeId', 'categoryId', 'dimension'];

export default function ExpenseReportsPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  // URL'den başlangıç durumu (paylaşılabilir link; mutlak tarihler kanonik)
  const initial = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const state = {};
    URL_FIELDS.forEach((f) => { const v = qs.get(f); if (v) state[f] = v; });
    if (!state.from || !state.to) Object.assign(state, presetRange(state.preset || 'thisMonth'));
    return state;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab] = useState(SLUGS.includes(initial.tab) ? initial.tab : 'department');
  const [period, setPeriod] = useState({
    preset: initial.preset || 'thisMonth',
    from: initial.from,
    to: initial.to,
    ...(initial.compareFrom ? { compareFrom: initial.compareFrom, compareTo: initial.compareTo || '' } : {}),
  });
  const [scope, setScope] = useState({
    locationId: initial.locationId || null,
    departmentId: initial.departmentId || null,
    employeeId: initial.employeeId || null,
    categoryId: initial.categoryId || null,
  });
  const [dimension, setDimension] = useState(DIMENSIONS.includes(initial.dimension) ? initial.dimension : 'total');

  const [cascade, setCascade] = useState({ departments: [], employees: [] });
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [exporting, setExporting] = useState(null);

  const queryParams = useMemo(() => ({
    from: period.from,
    to: period.to,
    compareFrom: period.compareFrom || null,
    compareTo: period.compareTo || null,
    ...scope,
    ...(tab === 'comparison' ? { dimension } : {}),
  }), [period, scope, tab, dimension]);

  // URL senkronu (paylaşılabilir görünüm)
  useEffect(() => {
    const qs = new URLSearchParams();
    qs.set('tab', tab);
    if (period.preset) qs.set('preset', period.preset);
    if (period.from) qs.set('from', period.from);
    if (period.to) qs.set('to', period.to);
    if (period.compareFrom) qs.set('compareFrom', period.compareFrom);
    if (period.compareTo) qs.set('compareTo', period.compareTo);
    Object.entries(scope).forEach(([k, v]) => { if (v) qs.set(k, v); });
    if (tab === 'comparison' && dimension !== 'total') qs.set('dimension', dimension);
    history.replace({ search: qs.toString() });
  }, [tab, period, scope, dimension, history]);

  // Kaskad kaynakları
  useEffect(() => {
    (async () => {
      try {
        const res = await getLocations({ pageSize: 200, orderBy: 'name' });
        setLocations(res.data?.data?.data ?? []);
      } catch { /* interceptor */ }
      try {
        const cats = await getExpenseCategories();
        setCategories(cats);
      } catch { /* interceptor */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getExpenseReportFilters({ locationId: scope.locationId, departmentId: scope.departmentId });
        setCascade(res.data?.data ?? { departments: [], employees: [] });
      } catch { /* interceptor */ }
    })();
  }, [scope.locationId, scope.departmentId]);

  // Rapor verisi. Payload hangi sekmeye aitse onunla birlikte saklanır: sekme
  // tıklaması fetch'ten ÖNCE render tetikler ve `data` o an hâlâ eski sekmenin
  // payload'ıdır — slug etiketi olmadan buildSections yeni slug'ı eski payload
  // ile çağırır ve sekmeye özgü alanlarda (matrix, byDepartment…) çöker.
  useEffect(() => {
    let stale = false;
    (async () => {
      setFetching(true);
      try {
        const res = await getExpenseReport(tab, queryParams);
        if (!stale) setData({ slug: tab, payload: res.data?.data ?? null });
      } catch {
        if (!stale) setData({ slug: tab, payload: null });
      } finally {
        if (!stale) setFetching(false);
      }
    })();
    return () => { stale = true; };
  }, [tab, queryParams]);

  const handleExport = useCallback(async (format) => {
    setExporting(format);
    try {
      const res = await exportExpenseReport(tab, { ...queryParams, format });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
      a.download = match ? match[1].replaceAll('"', '') : `masraf-${tab}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* interceptor */ } finally {
      setExporting(null);
    }
  }, [tab, queryParams]);

  // Slug uyuşmuyorsa veri henüz eski sekmenin: view kurulmaz, yükleniyor gösterilir.
  const dataIsStale = !data || data.slug !== tab;
  const view = useMemo(
    () => (!dataIsStale && data.payload ? buildSections(tab, data.payload, t, i18n.language) : null),
    [dataIsStale, tab, data, t, i18n.language],
  );

  return (
    <div>
      <Breadcrumbs items={[{ label: t('menu.expenseManagement') }, { label: t('menu.expenseReports') }]} />

      <Tabs
        tabs={SLUGS.map((slug) => ({ key: slug, label: t(`expenseReports.tabs.${slug}`) }))}
        activeTab={tab}
        onChange={setTab}
      />

      <FilterBar>
        <PeriodRangePicker value={period} onChange={setPeriod} />
        <Selects>
          <GenericSelect
            options={locations}
            value={scope.locationId}
            onChange={(v) => setScope((p) => ({ ...p, locationId: v || null, departmentId: null, employeeId: null }))}
            placeholder={t('expenseReports.filters.location')}
            size="small"
          />
          <GenericSelect
            options={cascade.departments}
            value={scope.departmentId}
            onChange={(v) => setScope((p) => ({ ...p, departmentId: v || null, employeeId: null }))}
            placeholder={t('expenseReports.filters.department')}
            size="small"
          />
          <GenericSelect
            options={cascade.employees}
            value={scope.employeeId}
            onChange={(v) => setScope((p) => ({ ...p, employeeId: v || null }))}
            placeholder={t('expenseReports.filters.employee')}
            size="small"
          />
          <GenericSelect
            options={categories}
            value={scope.categoryId}
            onChange={(v) => setScope((p) => ({ ...p, categoryId: v || null }))}
            placeholder={t('expenseReports.filters.category')}
            size="small"
          />
          {tab === 'comparison' && (
            <GenericSelect
              options={DIMENSIONS.map((d) => ({ id: d, name: t(`expenseReports.dimensions.${d}`) }))}
              value={dimension}
              onChange={(v) => setDimension(v || 'total')}
              placeholder={t('expenseReports.filters.dimension')}
              size="small"
            />
          )}
        </Selects>
        <ExportRow>
          {['xlsx', 'pdf', 'csv'].map((format) => (
            <ExportBtn
              key={format}
              variant="outline"
              disabled={exporting !== null}
              onClick={() => handleExport(format)}
            >
              <Download size={13} /> {format.toUpperCase()}
            </ExportBtn>
          ))}
        </ExportRow>
      </FilterBar>

      {fetching || dataIsStale ? (
        <Empty>{t('expenseReports.loading')}</Empty>
      ) : !view ? (
        <Empty>{t('expenseReports.empty')}</Empty>
      ) : (
        <>
          {view.kpis?.length > 0 && (
            <KpiRow>
              {view.kpis.map((kpi) => (
                <Kpi key={kpi.label}>
                  <div>{kpi.label}</div>
                  <div>{kpi.value}</div>
                </Kpi>
              ))}
            </KpiRow>
          )}
          {view.sections.map((section) => (
            <SectionCard key={section.title}>
              <SectionTitle>{section.title}</SectionTitle>
              {section.rows.length === 0 ? (
                <Empty>{t('expenseReports.sectionEmpty')}</Empty>
              ) : (
                <Table>
                  <thead>
                    <tr>{section.columns.map((c) => <th key={c}>{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={i}>
                        {row.map((cell, j) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <td key={j}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </SectionCard>
          ))}
          {view.footnotes?.map((note) => <Footnote key={note}>• {note}</Footnote>)}
        </>
      )}
    </div>
  );
}
